import React, { useEffect, useState } from 'react';
import { Modal, Input, Form, Button, InputNumber } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import type { OrderLineProps } from '@/types/commontypes';
import { ShapeType } from '@/types/commontypes';

interface DialogProps {
  showModal: boolean;
  onClose: () => void;
  onSave: (values: OrderLineProps) => void;
  defaultValues: Partial<OrderLineProps>;
}

export const Dialog: React.FC<DialogProps> = ({ showModal, onClose, onSave, defaultValues }) => {
  const [form] = Form.useForm();
  const [showTP, setShowTP] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      amount: defaultValues.amount ?? '',
      limitPrice: defaultValues.price ?? '',
      tpTrigger: defaultValues.tpsl?.[0]?.tp ?? '',
      slTrigger: defaultValues.tpsl?.[0]?.sl ?? '',
    });
  }, [defaultValues, form]);

  const handleConfirm = () => {
    form.validateFields().then(values => {
      const payload: OrderLineProps = {
        type: ShapeType.ORDER_LINE, // You may update this dynamically
        positionType: defaultValues.positionType ?? 'long', // fallback
        price: values.limitPrice,
        amount: Number(values.amount),
        tpsl:
          values.tpTrigger && values.slTrigger ? [{ tp: Number(values.tpTrigger), sl: Number(values.slTrigger) }] : [],
      };

      onSave(payload);
    });
  };

  const fixedAddon = (label: string) => <span style={{ display: 'inline-block', width: 70 }}>{label}</span>;

  return (
    <Modal
      title="Modify Parameters"
      open={showModal}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          Confirm
        </Button>,
      ]}
      width={280} // reduce modal width
      destroyOnHidden
    >
      <div style={{ maxWidth: 270, margin: '0 auto' }}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="limitPrice"
            rules={[
              { required: true, message: 'Please input the limit price!' },
              {
                pattern: /^[+-]?\d+(\.\d+)?$/,
                message: 'Please enter a valid number',
              },
            ]}
          >
            <InputNumber
              type="number"
              step="0.01"
              precision={2}
              addonBefore={fixedAddon('Limit Price')}
              suffix="USDT"
            />
          </Form.Item>

          <Form.Item
            name="amount"
            rules={[
              { required: true, message: 'Please input the amount!' },
              {
                pattern: /^[+-]?\d+(\.\d+)?$/,
                message: 'Please enter a valid number',
              },
            ]}
          >
            <Input type="number" step="0.01" addonBefore={fixedAddon('Amount')} suffix="USDT" />
          </Form.Item>

          <div
            onClick={() => setShowTP(!showTP)}
            style={{
              fontWeight: 500,
              fontSize: '14px',
              margin: '20px 0px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span>TP (%)</span>
            <span style={{ marginLeft: 7 }}>{showTP ? <UpOutlined /> : <DownOutlined />}</span>
          </div>

          {showTP && (
            <Form.Item
              name="tpTrigger"
              rules={[
                { required: true, message: 'Please input the TP trigger price!' },
                {
                  pattern: /^[+-]?\d+(\.\d+)?$/,
                  message: 'Please enter a valid number',
                },
              ]}
            >
              <InputNumber type="number" step="0.01" precision={2} addonBefore={fixedAddon('TP (%)')} suffix="%" />
            </Form.Item>
          )}

          <div
            style={{
              fontWeight: 500,
              fontSize: '14px',
              margin: '20px 0 10px',
            }}
          >
            Stop Loss
          </div>

          <Form.Item
            name="slTrigger"
            rules={[
              { required: true, message: 'Please input the SL trigger price!' },
              {
                pattern: /^[+-]?\d+(\.\d+)?$/,
                message: 'Please enter a valid number',
              },
            ]}
          >
            <InputNumber type="number" step="0.01" precision={2} addonBefore={fixedAddon('SL (%)')} suffix="%" />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};
